import Vue from "vue";
import _ from "lodash";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import { downloadCsvFile } from "@/shared/fileDownload";

import moment from "moment";
import { FetchState, HsabNotionalTransactionState, HsabTransactionDetail } from "@/shared/types";
import * as bankService from "@/shared/api/bankService";
import * as transactionUtility from "@/employee/Utilities/HsabTransactionUtility";

const pageSize = 10;

const emptyState: HsabNotionalTransactionState = {
    fetchStateDictionary: {},
    transactionsDictionary: {},
    page: 1,
    transactionFileExportFetchState: {}
};

function getFilteredTransactions (
    state: HsabNotionalTransactionState,
    accountId: number,
    key: string,
    creditDebitIndicator?: string)
    : HsabTransactionDetail[] {
    return _.has(state.transactionsDictionary, accountId)
        ? transactionUtility.filterTransactions(state.transactionsDictionary[accountId], key, creditDebitIndicator)
        : [];
}

function getTransactionsForPage (
    state: HsabNotionalTransactionState,
    accountId: number,
    key: string,
    creditDebitIndicator?: string)
    : HsabTransactionDetail[] {
    return getFilteredTransactions(state, accountId, key, creditDebitIndicator)
        .slice((state.page - 1) * pageSize, state.page * pageSize);
}

function getPageCount (
    state: HsabNotionalTransactionState,
    accountId: number,
    key: string,
    creditDebitIndicator?: string): number {
    return Math.max(Math.ceil(getFilteredTransactions(state, accountId, key, creditDebitIndicator).length / pageSize), 1);
}

export const useHsabNotionalAccountTransactionStore = defineStore("hsabNotionalAccountTransaction", {
    state: () => (_.cloneDeep(emptyState)),
    getters: {
        transactions: state => (accountId: number, key: string) =>
            getTransactionsForPage(state, accountId, key),
        transactionsPageCount: state => (accountId: number, key: string) =>
            getPageCount(state, accountId, key),
        transactionsTotalCount: state => (accountId: number, key: string) =>
            getFilteredTransactions(state, accountId, key).length,
        creditTransactions: state => (accountId: number, key: string) =>
            getTransactionsForPage(state, accountId, key, "C"),
        creditTransactionsPageCount: state => (accountId: number, key: string) =>
            getPageCount(state, accountId, key, "C"),
        creditTransactionsTotalCount: state => (accountId: number, key: string) =>
            getFilteredTransactions(state, accountId, key, "C").length,
        debitTransactions: state => (accountId: number, key: string) =>
            getTransactionsForPage(state, accountId, key, "D"),
        debitTransactionsPageCount: state => (accountId: number, key: string) =>
            getPageCount(state, accountId, key, "D"),
        debitTransactionsTotalCount: state => (accountId: number, key: string) =>
            getFilteredTransactions(state, accountId, key, "D").length,
        isLoaded: state => (accountId: number) =>
            _.has(state.fetchStateDictionary, accountId) && state.fetchStateDictionary[accountId] === FetchState.Loaded,
        isLoading: state => (accountId: number) =>
            _.has(state.fetchStateDictionary, accountId) && state.fetchStateDictionary[accountId] === FetchState.Loading,
        loadingFailed: state => (accountId: number) =>
            _.has(state.fetchStateDictionary, accountId) && state.fetchStateDictionary[accountId] === FetchState.Failed,
        isTransactionFileExportLoading: state => (accountId: number) =>
            state.transactionFileExportFetchState[accountId] === FetchState.Loading
    },
    actions: {
        setPageNumber (page: number): void {
            this.page = page;
        },
        setTransactions (accountId: number, transactions: HsabTransactionDetail[]): void {
            Vue.set(this.transactionsDictionary, accountId, transactions);
        },
        setTransactionsLoadingState (accountId: number, fetchState: FetchState): void {
            Vue.set(this.fetchStateDictionary, accountId, fetchState);
        },
        fetchTransactions (accountId: number): Promise<any> {
            if (_.has(this.fetchStateDictionary, accountId) &&
                this.fetchStateDictionary[accountId] !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setTransactionsLoadingState(accountId, FetchState.Loading);

            return bankService.getHsabNotionalTransactions(accountId)
                .then(response => {
                    this.setTransactions(accountId, response.data);
                    this.setTransactionsLoadingState(accountId, FetchState.Loaded);
                })
                .catch(err => {
                    this.setTransactionsLoadingState(accountId, FetchState.Failed);
                    return storeHelper.handleError({error: err, rethrow: false});
                });
        },
        setTransactionFileExportFetchState (accountId: number, fetchState: FetchState) {
            Vue.set(this.transactionFileExportFetchState, accountId, fetchState);
        },
        downloadTransactionExportFile (accountId: number, accountName: string): Promise<any> {
            this.setTransactionFileExportFetchState(accountId, FetchState.Loading);
            return bankService.getHsabNotionalTransactionExportFile(accountId).then(response => {
                downloadCsvFile(response.data, `${accountName.replaceAll(" ", "-")}-as-of-${moment().format("MM-DD-YYYY")}.csv`);
                this.setTransactionFileExportFetchState(accountId, FetchState.Loaded);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
                this.setTransactionFileExportFetchState(accountId, FetchState.Uninitialized);
            });
        }
    }
});







